// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// cwLogParse (#247) — pure parser turning a raw CloudWatch Logs event into a
// structured row for the in-app Log viewer.
//
// AWS Lambda (Node) log lines come in a few shapes:
//   • Application line (structured logging):
//       "<ISO>\t<requestId>\t<LEVEL>\t<text...>"
//     e.g. "2026-07-24T12:55:52.026Z\ta796...\tINFO\tapp handler event:  { ... }"
//   • Platform lines:
//       "START RequestId: <id> Version: $LATEST"
//       "END RequestId: <id>"
//       "REPORT RequestId: <id>  Duration: 12.3 ms  Billed Duration: 13 ms ..."
//       "INIT_START Runtime Version: ..."
//   • Anything else → treated as a plain INFO message.
//
// Many application lines embed a JSON object (e.g. the `handler event:` payload).
// We split that out so the viewer can pretty-print it collapsibly, exactly the
// forensic need that motivated #247 (#245's `reserved` vs `reserve` bug lived in
// one of those payloads).
//
// PURE — no side effects, no Date.now(); same input → same output. Exported so
// the viewer and its tests share one implementation.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const KNOWN_LEVELS = new Set([
  "TRACE",
  "DEBUG",
  "INFO",
  "WARN",
  "WARNING",
  "ERROR",
  "FATAL",
  "CRITICAL",
]);

// extractJsonObject(text) — if `text` contains a balanced JSON object, return
// { before, json, after } where `json` is the PARSED object; otherwise null.
// String/escape aware so a `{` or `}` inside a string literal doesn't fool the
// balance counter. Only the FIRST top-level object is extracted.
export function extractJsonObject(text) {
  if (typeof text !== "string") return null;
  const start = text.indexOf("{");
  if (start === -1) return null;

  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === "\\") esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) {
        const candidate = text.slice(start, i + 1);
        try {
          const json = JSON.parse(candidate);
          return {
            before: text.slice(0, start).trim(),
            json,
            after: text.slice(i + 1).trim(),
          };
        } catch {
          return null; // looked like JSON but isn't valid — leave as plain text
        }
      }
    }
  }
  return null; // never balanced
}

// classifyPlatformLine(message) — recognize the Lambda platform lines. Returns
// { level, requestId, text, meta } or null if not a platform line.
function classifyPlatformLine(message) {
  const m = message.trimStart();
  const reqOf = (s) => {
    const r = s.match(/RequestId:\s*([0-9a-f-]+)/i);
    return r ? r[1] : null;
  };
  if (m.startsWith("START RequestId:")) {
    return { level: "START", requestId: reqOf(m), text: m.trim(), meta: null };
  }
  if (m.startsWith("END RequestId:")) {
    return { level: "END", requestId: reqOf(m), text: m.trim(), meta: null };
  }
  if (m.startsWith("REPORT RequestId:")) {
    const dur = m.match(/\bDuration:\s*([\d.]+)\s*ms/);
    const billed = m.match(/\bBilled Duration:\s*([\d.]+)\s*ms/);
    const mem = m.match(/\bMax Memory Used:\s*([\d.]+)\s*MB/);
    return {
      level: "REPORT",
      requestId: reqOf(m),
      text: m.trim(),
      meta: {
        durationMs: dur ? Number(dur[1]) : null,
        billedMs: billed ? Number(billed[1]) : null,
        maxMemoryMb: mem ? Number(mem[1]) : null,
      },
    };
  }
  if (m.startsWith("INIT_START")) {
    return { level: "INIT", requestId: null, text: m.trim(), meta: null };
  }
  return null;
}

// parseCwLogEvent(event) — event is { timestamp, message, logStreamName, eventId }
// as returned by the Lambda `cwLogs` method. Returns a viewer row.
export function parseCwLogEvent(event) {
  const timestamp =
    typeof event?.timestamp === "number" ? event.timestamp : null;
  const raw = typeof event?.message === "string" ? event.message : "";
  const base = {
    id: event?.eventId ?? (timestamp != null ? String(timestamp) : ""),
    timestamp,
    logStreamName: event?.logStreamName ?? null,
    raw,
  };

  // Platform line?
  const platform = classifyPlatformLine(raw);
  if (platform) {
    return { ...base, ...platform, json: null };
  }

  // Tab-delimited application line: "<ISO>\t<requestId>\t<LEVEL>\t<text>"
  const parts = raw.split("\t");
  let level = "INFO";
  let requestId = null;
  let text = raw.replace(/\n$/, "");
  if (
    parts.length >= 4 &&
    ISO_RE.test(parts[0].trim()) &&
    KNOWN_LEVELS.has(parts[2].trim().toUpperCase())
  ) {
    requestId = parts[1].trim();
    level = parts[2].trim().toUpperCase();
    if (level === "WARNING") level = "WARN";
    text = parts.slice(3).join("\t").replace(/\n$/, "");
  }

  const extracted = extractJsonObject(text);
  return {
    ...base,
    level,
    requestId,
    meta: null,
    text: extracted ? extracted.before || text : text,
    json: extracted ? extracted.json : null,
  };
}

// LEVEL_ORDER / cwLevelColor — shared display helpers so the table and any
// charts agree on level styling.
export const CW_LEVELS = ["ERROR", "FATAL", "CRITICAL", "WARN", "INFO", "DEBUG", "TRACE"];

export function cwLevelColor(level) {
  const l = String(level || "").toUpperCase();
  if (l === "ERROR" || l === "FATAL" || l === "CRITICAL") return "#ef4444";
  if (l === "WARN" || l === "WARNING") return "#f59e0b";
  if (l === "REPORT") return "#8b5cf6";
  if (l === "START" || l === "END" || l === "INIT") return "#6b7280";
  return "#111827";
}
